class PredictiveSearch {
  constructor() {
    this.searchInput = null;
    this.searchResultsContainer = null;
    this.debounceTimer = null;
    this.debounceDelay = 300;
    this.isLoading = false;
    this.currentQuery = "";
    this.currentAbortController = null;
    this.maxRecentSearches = 4;
    this.recentSearchesKey = "shopify_recent_searches";

    this.init();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.initializeRecentSearches();
    this.toggleDefaultSuggestions();
  }

  cacheElements() {
    this.searchInput = document.querySelector(".search-input");
    this.searchResultsContainer = document.querySelector(
      ".search-results-container"
    );
    this.predictiveSearchContainer =
      document.querySelector(".predictive-search");
    this.recentSearchesContainer = document.querySelector(".recent-searches");
    this.recentSearchesList = document.querySelector(".recent-searches-list");
    this.searchLoadingContainer = document.querySelector(".search-loading");
    this.searchErrorContainer = document.querySelector(".search-error");
    this.searchClearBtn = document.querySelector(".search-clear-btn");
  }

  bindEvents() {
    if (this.searchInput) {
      this.searchInput.addEventListener("input", (e) => {
        this.handleSearchInput(e.target.value);
        this.toggleDefaultSuggestions();
        this.toggleClearButton();
      });

      this.searchInput.addEventListener("keyup", () => {
        this.toggleDefaultSuggestions();
        this.toggleClearButton();
      });

      this.searchInput.addEventListener("paste", () => {
        setTimeout(() => {
          this.toggleDefaultSuggestions();
          this.toggleClearButton();
        }, 10);
      });

      this.searchInput.addEventListener("focus", () => {
        if (this.currentQuery) {
          this.showResults();
        }
        this.toggleDefaultSuggestions();
        this.toggleClearButton();
        if (!this.searchInput.value.trim()) {
          this.renderRecentSearches();
        }
      });

      const searchForm = this.searchInput.closest("form");
      if (searchForm) {
        searchForm.addEventListener("submit", (e) => {
          const query = this.searchInput.value.trim();
          if (query) {
            this.saveRecentSearch(query);
          } else {
            e.preventDefault();
          }
        });
      }
    }

    if (this.searchClearBtn) {
      this.searchClearBtn.addEventListener("click", () => {
        this.clearSearch();
        this.searchInput?.focus();
      });
    }

    document.addEventListener("click", (e) => {
      const clickedInsideSearch = e.target.closest(".header-search");
      const clickedOnCartDrawer =
        e.target.closest("cart-drawer") ||
        e.target.closest(".cart-drawer-overlay") ||
        e.target.closest(".cart-drawer-container");
      const clickedOnProductOptions =
        e.target.closest(".product-option-container") ||
        e.target.closest(".product-option-overlay") ||
        e.target.closest("#product-options-relocation");

      if (
        !clickedInsideSearch &&
        !clickedOnCartDrawer &&
        !clickedOnProductOptions
      ) {
        this.hideResults();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.hideResults();
        this.searchInput?.blur();

        const searchContainer = document.querySelector(".header-search");
        if (searchContainer?.classList.contains("active")) {
          toggleSearch();
          document.querySelector('button[aria-label="Open search"]')?.focus();
        }
      }

      if (
        e.key === "Enter" &&
        this.searchInput &&
        document.activeElement === this.searchInput
      ) {
        const query = this.searchInput.value.trim();
        if (query) {
          this.saveRecentSearch(query);
          const searchForm = this.searchInput.closest("form");
          if (searchForm) {
            searchForm.submit();
          } else {
            window.location.href = `/search?q=${encodeURIComponent(
              query
            )}&type=product`;
          }
        }
      }
    });
  }

  toggleClearButton() {
    if (!this.searchClearBtn) return;

    const hasValue =
      this.searchInput && this.searchInput.value.trim().length > 0;
    this.searchClearBtn.style.display = hasValue ? "block" : "none";
  }

  handleSearchInput(query) {
    const trimmedQuery = query.trim();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (!trimmedQuery) {
      this.hideResults();
      this.currentQuery = "";
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.performSearch(trimmedQuery);
    }, this.debounceDelay);
  }

  async performSearch(query) {
    if (query === this.currentQuery) {
      return;
    }

    // Cancel any in-flight search so a fast typer's latest query always wins.
    this.currentAbortController?.abort();
    const controller = new AbortController();
    this.currentAbortController = controller;

    this.isLoading = true;
    this.currentQuery = query;
    this.showLoadingState();

    try {
      const url = `${
        window.location.origin
      }/search/suggest?q=${encodeURIComponent(
        query
      )}&resources[type]=product,collection,page,article,query&resources[limit]=10&section_id=predictive-search`;

      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      this.displayResults(html);

      this.saveRecentSearch(query);
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      console.error("Search error:", error);
      this.showErrorState();
    } finally {
      // A superseded call's finally still runs — only act if still current.
      if (this.currentAbortController === controller) {
        this.isLoading = false;
        this.currentAbortController = null;
        window.initializeProductCards?.(this.predictiveSearchContainer || document);
        window.initNotifyMeModal?.();
      }
    }
  }

  showLoadingState() {
    this.hideAllStates();
    if (this.searchLoadingContainer) {
      this.searchLoadingContainer.style.display = "block";
    }
  }

  showErrorState() {
    this.hideAllStates();
    if (this.searchErrorContainer) {
      this.searchErrorContainer.style.display = "block";
    }
  }

  hideAllStates() {
    if (this.searchLoadingContainer) {
      this.searchLoadingContainer.style.display = "none";
    }
    if (this.searchErrorContainer) {
      this.searchErrorContainer.style.display = "none";
    }
    if (this.recentSearchesContainer) {
      this.recentSearchesContainer.style.display = "none";
    }
  }

  displayResults(html) {
    if (!this.predictiveSearchContainer) {
      return;
    }
    this.storeDefaultContent();

    const recentSearchesElement =
      this.predictiveSearchContainer.querySelector(".recent-searches");

    this.predictiveSearchContainer.innerHTML = html;

    if (recentSearchesElement) {
      this.predictiveSearchContainer.insertBefore(
        recentSearchesElement,
        this.predictiveSearchContainer.firstChild
      );
    }

    this.showResults();
    this.bindResultEvents();

    if (typeof window.initializeLazyVideos === "function") {
      window.initializeLazyVideos(this.predictiveSearchContainer);
    }
  }

  storeDefaultContent() {
    if (!this.defaultContentHTML && this.predictiveSearchContainer) {
      this.defaultContentHTML = this.predictiveSearchContainer.innerHTML;
    }
  }

  restoreDefaultContent() {
    if (this.defaultContentHTML && this.predictiveSearchContainer) {
      this.predictiveSearchContainer.innerHTML = this.defaultContentHTML;

      if (typeof window.initializeLazyVideos === "function") {
        window.initializeLazyVideos(this.predictiveSearchContainer);
      }

      if (typeof window.initializeSearchProductsCarousel === "function") {
        window.initializeSearchProductsCarousel(this.predictiveSearchContainer);
      }
    }
  }

  showResults() {
    this.hideAllStates();
    if (this.searchResultsContainer) {
      this.searchResultsContainer.style.display = "block";
    }
  }

  hideResults() {
    // Must cancel both a pending debounced search and an in-flight fetch — either could resolve later and silently repopulate a closed dropdown.
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    this.currentAbortController?.abort();
    this.currentAbortController = null;

    this.hideAllStates();

    this.restoreDefaultContent();

    this.recentSearchesContainer = document.querySelector(".recent-searches");
    this.recentSearchesList = document.querySelector(".recent-searches-list");

    this.renderRecentSearches();

    // Always show the just-restored defaults, ignoring leftover input text.
    if (this.predictiveSearchContainer) {
      const defaultSuggestionsSection =
        this.predictiveSearchContainer.querySelector(
          ".default-suggestions-section"
        );
      const defaultProductsSuggestions =
        this.predictiveSearchContainer.querySelector(
          ".products-search-suggestions"
        );
      const defaultActivitySuggestions =
        this.predictiveSearchContainer.querySelector(
          ".search-activity-suggestions"
        );
      if (defaultSuggestionsSection) {
        defaultSuggestionsSection.style.display = "";
      }
      if (defaultProductsSuggestions) {
        defaultProductsSuggestions.style.display = "";
      }
      if (defaultActivitySuggestions) {
        defaultActivitySuggestions.style.display = "";
      }
    }

    if (typeof window.initializeProductCards === "function") {
      window.initializeProductCards(this.predictiveSearchContainer || document);
    }
  }

  isShowingSearchResults() {
    return !!this.predictiveSearchContainer?.querySelector(
      ".search-suggestions, .search-queries, .search-no-results"
    );
  }

  toggleDefaultSuggestions() {
    if (!this.searchInput) return;

    const hasValue = this.searchInput.value.trim().length > 0;

    // Must never touch the search-results markup itself, only recent searches.
    if (this.isShowingSearchResults()) {
      if (hasValue && this.recentSearchesContainer) {
        this.recentSearchesContainer.style.display = "none";
      }
      return;
    }

    const defaultSuggestionsSection =
      this.predictiveSearchContainer?.querySelector(
        ".default-suggestions-section"
      );

    const defaultProductsSuggestions =
      this.predictiveSearchContainer?.querySelector(
        ".products-search-suggestions"
      );

    const defaultActivitySuggestions =
      this.predictiveSearchContainer?.querySelector(
        ".search-activity-suggestions"
      );

    if (defaultSuggestionsSection) {
      defaultSuggestionsSection.style.display = hasValue ? "none" : "";
    }

    if (defaultProductsSuggestions) {
      defaultProductsSuggestions.style.display = hasValue ? "none" : "";
    }

    if (defaultActivitySuggestions) {
      defaultActivitySuggestions.style.display = hasValue ? "none" : "";
    }

    if (hasValue) {
      if (this.recentSearchesContainer) {
        this.recentSearchesContainer.style.display = "none";
      }
    } else {
      this.renderRecentSearches();
    }
  }

  bindResultEvents() {
    const searchItems = this.searchResultsContainer?.querySelectorAll(
      ".search-item a, .search-product-item a"
    );

    if (searchItems) {
      searchItems.forEach((item) => {
        item.addEventListener("click", (e) => {
          this.hideResults();
        });
      });
    }
  }

  initializeRecentSearches() {
    this.renderRecentSearches();
    this.bindRecentSearchEvents();
  }

  getRecentSearches() {
    try {
      const searches = localStorage.getItem(this.recentSearchesKey);
      const result = searches ? JSON.parse(searches) : [];
      return result;
    } catch (error) {
      console.error("Error getting recent searches:", error);
      return [];
    }
  }

  saveRecentSearch(query) {
    if (!query || query.trim().length === 0) return;

    const trimmedQuery = query.trim();
    let recentSearches = this.getRecentSearches();

    recentSearches = recentSearches.filter(
      (search) => search.toLowerCase() !== trimmedQuery.toLowerCase()
    );

    recentSearches.unshift(trimmedQuery);

    recentSearches = recentSearches.slice(0, this.maxRecentSearches);

    try {
      localStorage.setItem(
        this.recentSearchesKey,
        JSON.stringify(recentSearches)
      );
      this.renderRecentSearches();
    } catch (error) {
      console.error("Error saving recent search:", error);
    }
  }

  renderRecentSearches() {
    const recentSearches = this.getRecentSearches();

    if (!this.recentSearchesContainer) {
      return;
    }

    const hasInputValue =
      this.searchInput && this.searchInput.value.trim().length > 0;

    if (recentSearches.length === 0 || hasInputValue) {
      this.recentSearchesContainer.style.display = "none";
      return;
    }

    this.recentSearchesContainer.style.display = "block";

    if (this.recentSearchesList) {
      this.recentSearchesList.innerHTML = "";

      recentSearches.forEach((search) => {
        const searchItem = document.createElement("div");
        searchItem.className = "recent-search";
        searchItem.textContent = search;
        searchItem.addEventListener("click", () => {
          this.handleRecentSearchClick(search);
        });
        this.recentSearchesList.appendChild(searchItem);
      });
    }
  }

  bindRecentSearchEvents() {
    if (this.recentSearchesList) {
      this.recentSearchesList.addEventListener("click", (e) => {
        if (e.target.classList.contains("recent-search")) {
          const query = e.target.textContent;
          this.handleRecentSearchClick(query);
        }
      });
    }
  }

  handleRecentSearchClick(query) {
    if (this.searchInput) {
      this.searchInput.value = query;
      this.performSearch(query);
    }
  }

  clearRecentSearches() {
    try {
      localStorage.removeItem(this.recentSearchesKey);
      this.renderRecentSearches();
    } catch (error) {
      console.error("Error clearing recent searches:", error);
    }
  }

  clearSearch() {
    if (this.searchInput) {
      this.searchInput.value = "";
      this.toggleClearButton();
    }
    this.hideResults();
    this.currentQuery = "";
  }
}

function toggleSearch() {
  const searchContainer = document.querySelector(".header-search");
  const searchOverlay = document.querySelector(".header-search-overlay");
  if (searchContainer) {
    const wasActive = searchContainer.classList.contains("active");
    searchContainer.classList.toggle("active");
    if (searchOverlay) {
      searchOverlay.classList.toggle("active");
    }
    const isNowActive = searchContainer.classList.contains("active");
    if (isNowActive && !wasActive) {
      const currentScrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${currentScrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      const searchInput = document.querySelector(".search-input");
      if (searchInput) {
        searchInput.focus();
      }
    } else if (!isNowActive && wasActive) {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }
  }
}

let predictiveSearchInstance;
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    predictiveSearchInstance = new PredictiveSearch();
  });
} else {
  predictiveSearchInstance = new PredictiveSearch();
}

window.clearSearch = function () {
  predictiveSearchInstance?.clearSearch();
};
